import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { useFormatValue } from "../../../hooks/useFormatValue";
import { translate } from "../../../hooks/translate";
import { useModalConfirm as confirm } from "../../../hooks/useModalConfirm";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { dateStrInAppTz } from "../../../utils/appTime";
import { withAuth } from "../../../components/HOC/withAuth";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import { useTitle } from "../../../hooks/useTitle";

import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import Input from "../../../components/BaseUI/Input";
import Button from "../../../components/BaseUI/Button";
import Select from "../../../components/BaseUI/Select";
import Label from "../../../components/BaseUI/Label";
import Lozenge from "../../../components/BaseUI/Lozenge";
import Checkbox from "../../../components/BaseUI/Checkbox";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import DataFilters from "../../../components/CustomUI/DataFilters";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
} from "../../../components/BaseUI/Dropdown";
import {
  FiEdit3,
  FiTrash2,
  FiServer,
  FiPlus,
  FiSave,
  FiCalendar,
  FiDollarSign,
  FiCreditCard,
  FiPackage,
  FiShoppingCart,
  FiActivity,
  FiMessageSquare,
  FiInfo,
  FiCheck,
  FiX,
} from "react-icons/fi";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const ProducedDevices = () => {
  const [devices, setDevices] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [models, setModels] = useState([]);
  const [allDevices, setAllDevices] = useState([]);
  const [activeTab, setActiveTab] = useState("details");

  // Filter states
  const [selectedFilterModel, setSelectedFilterModel] = useState(null);
  const [selectedFilterStatus, setSelectedFilterStatus] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Production states
  const [showProductionModal, setShowProductionModal] = useState(false);
  const [productionForm, setProductionForm] = useState({
    modelId: "",
    quantity: 1,
    batchNumber: "",
    notes: "",
  });

  // Selection states
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [saleForm, setSaleForm] = useState({
    dealerId: null,
    companyId: null,
    saleDate: dateStrInAppTz(new Date()),
    paymentMethod: "cash",
    paymentStatus: "pending",
    status: "completed",
    notes: "",
  });

  // Additional states for sale functionality
  const [dealers, setDealers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [pricePerDevice, setPricePerDevice] = useState(0);
  const [deviceModels, setDeviceModels] = useState([]);
  const headers = [
    { key: "select", name: "Seç", dataType: "checkbox", id: -1 },
    { key: "hardwareId", name: "Hardware ID", dataType: "text", id: 0 },
    { key: "serialNumber", name: "Seri No", dataType: "text", id: 1 },
    { key: "modelName", name: "Model", dataType: "text", id: 2 },
    { key: "status", name: "Durum", dataType: "option", id: 3 },
    { key: "productionDate", name: "Üretim Tarihi", dataType: "text", id: 5 },
    { key: "actions", name: "İşlemler", dataType: "actions", id: 6 },
  ];
  // Column visibility
  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("produced-devices-list", headers);
  const format = useFormatValue;
  const navigate = useNavigate();
  const { getToken, getUser, getUserFromToken } = useAuth();
  const user = getUser();
  const canWrite =
    !!user &&
    hasPageWriteAccess(
      user.role,
      "/produced-devices",
      user.pages || [],
      !!user.fullAccess,
    );

  useTitle("Rovion MES - Üretilen Ürünler");

  // Fetch devices
  const fetchDevices = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/devices`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Handle response structure: could be array or { devices, pagination }
      const responseData = Array.isArray(response.data)
        ? response.data
        : response.data?.devices || [];

      // Filter for production devices (inactive, production, shipped statuses)
      const productionDevices = responseData.filter((device) =>
        ["production", "production_active"].includes(device.status),
      );

      setDevices(productionDevices);
      setAllDevices(productionDevices);
    } catch (error) {
      console.error("Error fetching produced devices:", error);
      setDevices([]);
      setAllDevices([]);
      addToast("Üretilen Ürünler getirilirken hata oluştu", "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch device models
  const fetchModels = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/device-models`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setModels(response.data || []);
    } catch (error) {
      console.error("Error fetching models:", error);
      setModels([]); // Ensure models is always an array
      addToast("Model listesi getirilirken hata oluştu", "error");
    }
  };

  useEffect(() => {
    fetchDevices();
    fetchModels();
    fetchDealers();
    fetchCompanies();
    fetchDeviceModels();
  }, []);

  // Filter devices and reset selections when filters change
  useEffect(() => {
    let filtered = allDevices;

    if (searchQuery) {
      filtered = filtered.filter(
        (device) =>
          device.hardwareId
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          device.serialNumber
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          device.modelId?.name
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          device.modelId?.code
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          device.batchNumber?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    if (selectedFilterModel) {
      filtered = filtered.filter(
        (device) => device.modelId?._id === selectedFilterModel,
      );
    }

    if (selectedFilterStatus) {
      filtered = filtered.filter(
        (device) => device.status === selectedFilterStatus,
      );
    }

    setDevices(filtered);

    // Reset selections when devices list changes
    setSelectedDevices([]);
    setSelectAll(false);
  }, [searchQuery, selectedFilterModel, selectedFilterStatus, allDevices]);

  // Toast functions
  const addToast = (message, type = "success") => {
    const toast = {
      id: Date.now(),
      message,
      type,
    };
    setToasts((prev) => [...prev, toast]);
    setTimeout(() => {
      removeToast(toast.id);
    }, 5000);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  // Device actions
  const openDeviceDetails = (device) => {
    // Check if device is in production_active status
    if (device.status === "production_active") {
      addToast(
        `${device.hardwareId} (${device.serialNumber}) ürünü şu anda aktif üretimde olduğu için düzenlenemez. Lütfen üretim tamamlandıktan sonra tekrar deneyin.`,
        "warning",
      );
      return;
    }

    setDeviceInfo(device);
    setActiveTab("details");
    setSidebarOpened(true);
  };

  const closeSidebar = () => {
    setSidebarOpened(false);
    setDeviceInfo({});
  };

  // Production actions
  const handleAddProduction = () => {
    setProductionForm({
      modelId: "",
      quantity: 1,
      batchNumber: "",
      notes: "",
    });
    setShowProductionModal(true);
  };

  const handleProductionSubmit = async () => {
    try {
      if (!productionForm.modelId || !productionForm.quantity) {
        addToast("Model ve miktar alanları zorunludur", "error");
        return;
      }

      const token = getToken();
      const createdDevices = [];

      // Create devices one by one based on quantity
      for (let i = 0; i < productionForm.quantity; i++) {
        const timestamp = Date.now();
        const deviceData = {
          hardwareId: `HW_${timestamp}_${String(i).padStart(3, "0")}`, // Generate unique hardware ID
          macAddress: generateMacAddress(), // Generate MAC address
          serialNumber: `SN_${productionForm.batchNumber || timestamp}_${String(
            i,
          ).padStart(3, "0")}`, // Generate serial number
          modelId: productionForm.modelId,
          status: "production",
          batchNumber: productionForm.batchNumber || `BATCH_${timestamp}`,
          notes: productionForm.notes,
        };

        try {
          const response = await axios.post(
            `${API_URL}/api/devices`,
            deviceData,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );

          if (response.data.success) {
            createdDevices.push(response.data.device);
          }
        } catch (deviceError) {
          console.error(`Error creating device ${i + 1}:`, deviceError);
          // Continue with other devices even if one fails
        }

        // Small delay to ensure unique timestamps
        await new Promise((resolve) => setTimeout(resolve, 10));
      }

      addToast(
        `${createdDevices.length} adet ürün başarıyla oluşturuldu`,
        "success",
      );
      setShowProductionModal(false);
      fetchDevices();
    } catch (error) {
      console.error("Error adding production:", error);
      addToast("Üretim eklenirken hata oluştu", "error");
    }
  };

  // Helper function to generate MAC address
  const generateMacAddress = () => {
    const hex = "0123456789ABCDEF";
    let mac = "";
    for (let i = 0; i < 6; i++) {
      if (i > 0) mac += ":";
      mac += hex[Math.floor(Math.random() * 16)];
      mac += hex[Math.floor(Math.random() * 16)];
    }
    return mac;
  };

  // Fetch dealers
  const fetchDealers = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/dealers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDealers(response.data || []);
    } catch (error) {
      console.error("Error fetching dealers:", error);
      setDealers([]);
      addToast("Bayi listesi getirilirken hata oluştu", "error");
    }
  };

  // Fetch companies
  const fetchCompanies = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/companies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanies(response.data || []);
    } catch (error) {
      console.error("Error fetching companies:", error);
      setCompanies([]);
      addToast("Firma listesi getirilirken hata oluştu", "error");
    }
  };

  // Fetch device models
  const fetchDeviceModels = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/device-models`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeviceModels(response.data || []);
    } catch (error) {
      console.error("Error fetching device models:", error);
      setDeviceModels([]);
      addToast("Ürün modelleri getirilirken hata oluştu", "error");
    }
  };

  // Selection functions
  const handleSelectAll = (checked) => {
    if (checked) {
      // Only select devices that are not in production_active status
      const selectableDevices = devices.filter(
        (device) => device.status !== "production_active",
      );
      const deviceIds = selectableDevices.map((device) => device._id);
      setSelectedDevices(deviceIds);
      setSelectAll(true);

      // Show warning if there are production_active devices
      const productionActiveDevices = devices.filter(
        (device) => device.status === "production_active",
      );
      if (productionActiveDevices.length > 0) {
        addToast(
          `${productionActiveDevices.length} adet ürün aktif üretimde olduğu için seçilemedi.`,
          "warning",
        );
      }
    } else {
      setSelectedDevices([]);
      setSelectAll(false);
    }
  };

  const handleSelectDevice = (deviceId, checked) => {
    // Find the device to check its status
    const device = devices.find((d) => d._id === deviceId);

    if (checked) {
      // Check if device is in production_active status
      if (device && device.status === "production_active") {
        addToast(
          `${device.hardwareId} (${device.serialNumber}) ürünü şu anda aktif üretimde olduğu için siparişa uygun değildir. Lütfen üretim tamamlandıktan sonra tekrar deneyin.`,
          "warning",
        );
        return;
      }
      setSelectedDevices((prev) => [...prev, deviceId]);
    } else {
      setSelectedDevices((prev) => prev.filter((id) => id !== deviceId));
      setSelectAll(false);
    }
  };

  // Update selectAll when individual selections change
  useEffect(() => {
    // Only consider selectable devices (not production_active)
    const selectableDevices = devices.filter(
      (device) => device.status !== "production_active",
    );
    if (
      selectableDevices.length > 0 &&
      selectedDevices.length === selectableDevices.length
    ) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedDevices, devices]);

  const updateDeviceStatus = async (deviceId, newStatus) => {
    try {
      const token = getToken();
      await axios.put(
        `${API_URL}/api/devices/${deviceId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      addToast("Ürün durumu güncellendi", "success");
      fetchDevices();
    } catch (error) {
      console.error("Error updating device status:", error);
      addToast("Ürün durumu güncellenirken hata oluştu", "error");
    }
  };

  // Edit device functionality similar to main Devices page
  const handleDeviceInfo = async (device) => {
    // Check if device is in production_active status
    if (device.status === "production_active") {
      addToast(
        `${device.hardwareId} (${device.serialNumber}) ürünü şu anda aktif üretimde olduğu için düzenlenemez. Lütfen üretim tamamlandıktan sonra tekrar deneyin.`,
        "warning",
      );
      return;
    }

    try {
      setActiveTab("details");
      const response = await axios.get(
        `${API_URL}/api/devices/${device.hardwareId}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      const deviceData = response.data.device;
      setDeviceInfo({
        ...deviceData,
        modelId: deviceData.modelId,
        selectedModel: deviceData.modelId,
        originalStatus: deviceData.status,
      });
      setSidebarOpened(true);
    } catch (error) {
      console.error("Ürün detayı getirme hatası:", error);
      addToast("Ürün detayları yüklenirken bir hata oluştu.", "error");
    }
  };

  // Update device functionality similar to main Devices page
  const handleDeviceUpdate = async () => {
    try {
      setLoading(true);

      // MAC address format validation
      const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
      if (deviceInfo.macAddress && !macRegex.test(deviceInfo.macAddress)) {
        addToast(
          "Geçersiz MAC adresi formatı. Örnek: 00:11:22:33:44:55",
          "error",
        );
        return;
      }

      // Get current user data
      const userData = user || (await getUserFromToken(true));

      // Prepare update data
      const updateData = {
        hardwareId: deviceInfo.hardwareId,
        macAddress: deviceInfo.macAddress,
        serialNumber: deviceInfo.serialNumber,
        modelId: deviceInfo.modelId?._id,
        status: deviceInfo.status,
      };

      // Check if status has changed and add to logs if it has
      if (
        deviceInfo.originalStatus &&
        deviceInfo.originalStatus !== deviceInfo.status
      ) {
        const logEntry = {
          status: deviceInfo.status,
          previousStatus: deviceInfo.originalStatus,
          changedBy: userData._id,
          notes:
            deviceInfo.statusChangeNotes ||
            `Status changed from ${deviceInfo.originalStatus} to ${deviceInfo.status}`,
        };
        updateData.logEntry = logEntry;
      }

      // Assign dealerId or companyId based on user role
      if (userData) {
        if (deviceInfo.dealerId) {
          updateData.dealerId = deviceInfo.dealerId;
        } else if (
          (userData.role === "dealer" && userData.dealerId) ||
          (userData.role == "user" && userData.dealerId)
        ) {
          updateData.dealerId = userData.dealerId;
        }

        if (deviceInfo.companyId) {
          updateData.companyId = deviceInfo.companyId;
        } else if (
          (userData.role === "company" && userData.companyId) ||
          (userData.role == "user" && userData.companyId)
        ) {
          updateData.companyId = userData.companyId;
        }
      }

      const response = await axios.put(
        `${API_URL}/api/devices/${deviceInfo.hardwareId}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      if (response.data.success) {
        addToast("Ürün başarıyla güncellendi", "success");
        await fetchDevices();
        setSidebarOpened(false);
      } else {
        addToast(
          response.data.message || "Ürün güncellenirken bir hata oluştu.",
          "error",
        );
      }
    } catch (error) {
      console.error("Ürün güncelleme hatası:", error);
      addToast(
        error.response?.data?.message || "Ürün güncellenirken bir hata oluştu.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // Delete device functionality similar to main Devices page
  const deleteDevice = async (hardwareId) => {
    const device = devices?.find((device) => device.hardwareId === hardwareId);

    const confirmed = await confirm({
      title: `${translate("UI.RESET")} ${device?.hardwareId}?`,
      message:
        'Bu işlem ürünün tüm bilgilerini sıfırlayacak ve ürün "bekleyen" durumuna geçecektir.',
      confirmText: translate("UI.RESET"),
      cancelText: "İptal",
    });

    if (confirmed) {
      try {
        // Get current user data
        const userData = user || (await getUserFromToken(true));

        // Prepare reset data
        const resetData = {
          hardwareId: hardwareId,
          macAddress: "",
          serialNumber: "",
          modelId: null,
          status: "pending",
        };

        // Add log entry for device reset
        const logEntry = {
          status: "pending",
          previousStatus: device.status,
          changedBy: userData._id,
          notes: "Device reset to pending state",
        };

        resetData.logEntry = logEntry;

        // Assign dealerId or companyId based on user role
        if (userData) {
          if (device.dealerId) {
            resetData.dealerId = device.dealerId;
          } else if (
            (userData.role === "dealer" && userData.dealerId) ||
            (userData.role == "user" && userData.dealerId)
          ) {
            resetData.dealerId = userData.dealerId;
          }

          if (device.companyId) {
            resetData.companyId = device.companyId;
          } else if (
            (userData.role === "company" && userData.companyId) ||
            (userData.role == "user" && userData.companyId)
          ) {
            resetData.companyId = userData.companyId;
          }
        }

        const response = await axios.put(
          `${API_URL}/api/devices/${hardwareId}`,
          resetData,
          {
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
          },
        );

        if (response.data.success) {
          await fetchDevices();
          addToast(
            "Ürün başarıyla sıfırlandı ve bekleme durumuna alındı.",
            "success",
          );
        } else {
          addToast(
            response.data.message || "Ürün sıfırlanırken bir hata oluştu.",
            "error",
          );
        }
      } catch (error) {
        console.error("Ürün sıfırlama hatası:", error);
        addToast(
          error.response?.data?.message ||
            "Ürün sıfırlanırken bir hata oluştu.",
          "error",
        );
      }
    }
  };

  // Sale functions similar to CreateDeviceSale
  const handleSaleSubmit = async () => {
    try {
      setLoading(true);

      if (!saleForm.dealerId) {
        addToast("Lütfen bir bayi seçin.", "error");
        return;
      }

      if (selectedDevices.length === 0) {
        addToast("Lütfen en az bir ürün seçin.", "error");
        return;
      }

      // Convert selectedDevices IDs to device objects with prices for validation
      const selectedDeviceObjects = selectedDevices.map((deviceId) => {
        const device = devices.find((d) => d._id === deviceId);
        return { ...device, price: pricePerDevice };
      });

      // Check if all devices have valid prices (matching CreateDeviceSale logic)
      const hasInvalidPrice = selectedDeviceObjects.some(
        (device) => !device || !device.price || device.price <= 0,
      );
      if (hasInvalidPrice) {
        addToast("Lütfen tüm ürünler için geçerli bir fiyat girin.", "error");
        return;
      }

      const totalAmount = calculateTotalAmount();

      // Get user info from token for logs
      const decodedToken = JSON.parse(atob(getToken().split(".")[1]));
      const userId = decodedToken._id;

      // Match CreateDeviceSale payload structure exactly
      const salePayload = {
        dealerId: saleForm.dealerId,
        companyId: saleForm.companyId,
        saleDate: saleForm.saleDate,
        devices: selectedDeviceObjects
          .filter((device) => device && device._id)
          .map((device) => ({
            deviceId: device._id,
            serialNumber: device.serialNumber || "",
            price: device.price || 0,
          })),
        totalAmount: totalAmount,
        paymentMethod: saleForm.paymentMethod,
        paymentStatus: saleForm.paymentStatus,
        status: saleForm.status,
        notes: saleForm.notes || "",
      };
      const response = await axios.post(
        `${API_URL}/api/device-sales`,
        salePayload,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      if (response.data.success) {
        addToast(
          response.data.message || "Stok başarıyla oluşturuldu.",
          "success",
        );

        // Reset form and selections
        setSaleForm({
          dealerId: null,
          companyId: null,
          saleDate: dateStrInAppTz(new Date()),
          paymentMethod: "cash",
          paymentStatus: "pending",
          status: "completed",
          notes: "",
        });
        setPricePerDevice(0);
        setSelectedDevices([]);
        setSelectAll(false);
        setShowSaleModal(false);
        fetchDevices();
      } else {
        throw new Error(
          response.data.message || "Stok Aktarımı oluşturulamadı",
        );
      }
    } catch (error) {
      console.error("Stok oluşturma hatası:", error);
      addToast(
        error.response?.data?.message || "Stok kaydedilirken bir hata oluştu.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // Calculate total amount
  const calculateTotalAmount = () => {
    return pricePerDevice * selectedDevices.length;
  };

  // Handle price per device change
  const handlePricePerDeviceChange = (value) => {
    const newPrice = parseFloat(value) || 0;
    setPricePerDevice(newPrice);
  };

  // Sale form handlers similar to CreateDeviceSale
  const handleDealerSelect = (dealerId) => {
    setSaleForm((prev) => ({
      ...prev,
      dealerId: dealerId,
    }));
  };

  const handleCompanySelect = (companyId) => {
    setSaleForm((prev) => ({
      ...prev,
      companyId: companyId || null,
    }));
  };

  const handleDateChange = (value) => {
    setSaleForm((prev) => ({
      ...prev,
      saleDate: value,
    }));
  };

  const handlePaymentMethodChange = (method) => {
    setSaleForm((prev) => ({
      ...prev,
      paymentMethod: method,
    }));
  };

  const handlePaymentStatusChange = (status) => {
    setSaleForm((prev) => ({
      ...prev,
      paymentStatus: status,
    }));
  };

  const handleSaleStatusChange = (status) => {
    setSaleForm((prev) => ({
      ...prev,
      status: status,
    }));
  };

  const handleNotesChange = (value) => {
    setSaleForm((prev) => ({
      ...prev,
      notes: value,
    }));
  };

  // Status appearance functions
  const getPaymentStatusAppearance = (status) => {
    switch (status) {
      case "paid":
        return "success-subtle";
      case "pending":
        return "warning-subtle";
      case "partial":
        return "info-subtle";
      case "refunded":
        return "danger-subtle";
      default:
        return "neutral-subtle";
    }
  };

  const getSaleStatusAppearance = (status) => {
    switch (status) {
      case "completed":
        return "success-subtle";
      case "pending":
        return "warning-subtle";
      case "cancelled":
        return "danger-subtle";
      default:
        return "neutral-subtle";
    }
  };

  const getStatusLozenge = (status) => {
    switch (status) {
      case "active":
        return "success-subtle";
      case "inactive":
        return "danger-subtle";
      case "maintenance":
        return "warning-subtle";
      case "sold":
        return "info-subtle";
      case "production":
        return "warning-subtle";
      case "production_active":
        return "success-subtle";
      default:
        return "neutral-subtle";
    }
  };

  const handleProductionActive = async (device, production_active) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/devices/${device.hardwareId}/production-active`,
        { production_active },
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        },
      );
      if (response.data.success) {
        addToast(response.data.message, "success");
        fetchDevices();
      } else {
        addToast(response.data.message, "error");
      }
    } catch (error) {
      console.error("Üretim aktivasyonu hatası:", error);
      addToast(
        error.response?.data?.message || "Üretim aktivasyonu hatası",
        "error",
      );
    }
  };

  return (
    <AppPage>
      <Container>
        <ContainerItem>
          <div className="page-header-card p-3 d-flex justify-content-between align-items-center mb-4">
            <h2 className="mb-0">{translate("UI.PRODUCTION_INVENTORY")}</h2>
            <div className="d-flex gap-2 align-items-center">
              {canWrite && selectedDevices.length > 0 && (
                <>
                  <Button
                    appearance="primary-animated"
                    onClick={() => setShowSaleModal(true)}
                  >
                    <FiShoppingCart size={16} className="mr-2" />
                    Stok aktar ({selectedDevices.length})
                  </Button>
                  <Button
                    appearance="subtle"
                    onClick={() => {
                      setSelectedDevices([]);
                      setSelectAll(false);
                    }}
                  >
                    Seçimi Temizle
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Table */}
          <Table
            columns={headers}
            visibleColumns={visibleColumns}
            loading={loading}
            emptyMessage="Üretim ürünü bulunamadı"
            selectAll={selectAll}
            onSelectAll={handleSelectAll}
            showSelectAll={
              devices.filter((device) => device.status !== "production_active")
                .length > 0
            }
            viewOptions={
              <ListViewOptionsPanel
                columns={headers}
                visibleColumns={visibleColumns}
                onToggleColumn={toggleColumnVisibility}
                onResetColumns={resetToDefault}
                showApply={false}
                showReset={false}
              />
            }
          >
            {devices.map((device) => (
              <Tr key={device._id}>
                {visibleColumns.includes(-1) && (
                  <Td className="text-center">
                    <div className="d-flex justify-content-center mr-4">
                      <Checkbox
                        isChecked={selectedDevices.includes(device._id)}
                        disabled={device.status === "production_active"}
                        onChange={({ checked }) => {
                          handleSelectDevice(device._id, checked);
                        }}
                      />
                    </div>
                  </Td>
                )}
                {visibleColumns.includes(0) && (
                  <Td>{device.hardwareId || "-"}</Td>
                )}
                {visibleColumns.includes(1) && (
                  <Td>{device.serialNumber || "-"}</Td>
                )}
                {visibleColumns.includes(2) && (
                  <Td>
                    <div>
                      <div className="fw-medium">
                        {device.modelId?.name || "-"}
                      </div>
                      <small className="text-muted">
                        {device.modelId?.code || "-"}
                      </small>
                    </div>
                  </Td>
                )}
                {visibleColumns.includes(3) && (
                  <Td>
                    <Lozenge appearance={getStatusLozenge(device.status)}>
                      {translate(`UI.${device.status.toUpperCase()}`)}
                    </Lozenge>
                  </Td>
                )}
                {visibleColumns.includes(5) && (
                  <Td>
                    {device.createdAt ? device.createdAt.split("T")[0] : "-"}
                  </Td>
                )}
                {visibleColumns.includes(6) && (
                  <Td>
                    <div className="d-flex gap-1">
                      {canWrite && (
                        <>
                          <FiEdit3
                            className="cursor-pointer text-grey"
                            style={{ fontSize: "20px", cursor: "pointer" }}
                            shape="circle"
                            onClick={() => handleDeviceInfo(device)}
                            title={translate("UI.EDIT")}
                          />
                          <FiTrash2
                            className="cursor-pointer text-grey"
                            style={{ fontSize: "20px", cursor: "pointer" }}
                            shape="circle"
                            onClick={() => deleteDevice(device.hardwareId)}
                            title={translate("UI.DELETE")}
                          />
                        </>
                      )}
                      {device.status !== "production_active" && (
                        <FiCheck
                          className="cursor-pointer text-grey"
                          style={{ fontSize: "20px", cursor: "pointer" }}
                          shape="circle"
                          onClick={() => handleProductionActive(device, true)}
                          title={translate("UI.PRODUCTION_ACTIVE")}
                        />
                      )}
                      {device.status === "production_active" && (
                        <FiX
                          className="cursor-pointer text-grey"
                          style={{ fontSize: "20px", cursor: "pointer" }}
                          shape="circle"
                          onClick={() => handleProductionActive(device, false)}
                          title={translate("UI.PRODUCTION_INACTIVE")}
                        />
                      )}
                    </div>
                  </Td>
                )}
              </Tr>
            ))}
          </Table>

          {devices.length === 0 && !loading && (
            <div className="empty-state-container">
              <div className="empty-state-content">
                <div className="empty-state-icon">
                  <FiPackage size={64} />
                </div>
                <div className="empty-state-text">
                  <h4 className="empty-state-title">
                    Henüz üretim ürünü bulunmamaktadır
                  </h4>
                </div>
              </div>
            </div>
          )}
        </ContainerItem>
      </Container>

      {/* Device Details Sidebar */}
      <SidebarContainer
        show={sidebarOpened}
        title={translate("UI.DEVICE_DETAILS")}
        onClose={closeSidebar}
      >
        <div className="d-flex flex-column">
          <div className="device-header">
            <FiServer
              className="text-primary"
              style={{ width: "24px", height: "24px" }}
            />
            <div>
              <h5 className="mb-0">{deviceInfo?.hardwareId}</h5>
              <small className="text-muted">
                {deviceInfo?.modelId?.code || deviceInfo?.modelCode || "-"}
              </small>
            </div>
          </div>

          {/* Add tabs for details and logs */}
          <div className="device-tabs mb-3">
            <div className="custom-tabs">
              <button
                className={`tab-button ${
                  activeTab === "details" ? "active" : ""
                }`}
                onClick={() => setActiveTab("details")}
              >
                <FiServer className="tab-icon" />
                <span>{translate("UI.DETAILS")}</span>
              </button>
              <button
                className={`tab-button ${activeTab === "logs" ? "active" : ""}`}
                onClick={() => setActiveTab("logs")}
              >
                <FiActivity className="tab-icon" />
                <span>{translate("UI.LOGS")}</span>
                {deviceInfo?.logs?.length > 0 && (
                  <span className="tab-badge">{deviceInfo.logs.length}</span>
                )}
              </button>
            </div>
          </div>

          {/* Details tab */}
          {activeTab === "details" && (
            <div className="form-container">
              <div className="form-group">
                <Label>{translate("UI.STATUS")}</Label>
                <div className="form-control p-0">
                  <Dropdown
                    placement="bottom-start"
                    trigger={
                      <div className="d-flex align-items-center justify-content-between p-2 w-100">
                        <div className="d-flex align-items-center gap-2">
                          <Lozenge
                            appearance={getStatusLozenge(deviceInfo?.status)}
                          >
                            {translate(
                              `UI.${deviceInfo?.status?.toUpperCase()}`,
                            )}
                          </Lozenge>
                        </div>
                      </div>
                    }
                  >
                    <DropdownContent className="w-100">
                      <DropdownItem
                        onClick={() =>
                          setDeviceInfo((prev) => ({
                            ...prev,
                            status: "production",
                          }))
                        }
                        active={deviceInfo?.status === "production"}
                      >
                        <Lozenge appearance="warning-subtle">
                          {translate("UI.PRODUCTION")}
                        </Lozenge>
                      </DropdownItem>
                    </DropdownContent>
                  </Dropdown>
                </div>
              </div>

              {/* Show notes field when status is changed */}
              {deviceInfo?.originalStatus &&
                deviceInfo?.status !== deviceInfo?.originalStatus && (
                  <div className="form-group">
                    <Label>{translate("UI.STATUS_CHANGE_NOTES")}</Label>
                    <Input
                      type="textarea"
                      value={deviceInfo?.statusChangeNotes || ""}
                      placeholder={translate("UI.ENTER_STATUS_CHANGE_REASON")}
                      onChange={(value) =>
                        setDeviceInfo((prev) => ({
                          ...prev,
                          statusChangeNotes: value,
                        }))
                      }
                    />
                  </div>
                )}

              <div className="form-group">
                <Input
                  type="text"
                  value={deviceInfo?.hardwareId}
                  label={translate("UI.HARDWARE_ID")}
                  onChange={(value) =>
                    setDeviceInfo((prev) => ({ ...prev, hardwareId: value }))
                  }
                  required
                />
              </div>

              <div className="form-group">
                <Input
                  type="text"
                  value={deviceInfo?.serialNumber}
                  label={translate("UI.SERIAL_NUMBER")}
                  onChange={(value) =>
                    setDeviceInfo((prev) => ({ ...prev, serialNumber: value }))
                  }
                />
              </div>

              <div className="form-group">
                <Label required>{translate("UI.MODEL")}</Label>
                <div className="form-control p-0">
                  <Dropdown
                    placement="bottom-start"
                    trigger={
                      <div className="d-flex align-items-center justify-content-between p-2 w-100">
                        <span>
                          {models.find(
                            (m) => m._id === deviceInfo?.modelId?._id,
                          )?.code ||
                            models.find((m) => m._id === deviceInfo?.modelId)
                              ?.code ||
                            translate("UI.SELECT_MODEL")}
                        </span>
                      </div>
                    }
                  >
                    <DropdownContent className="w-100">
                      {models.map((model) => (
                        <DropdownItem
                          key={model._id}
                          onClick={() =>
                            setDeviceInfo((prev) => ({
                              ...prev,
                              modelId: model,
                              selectedModel: model,
                            }))
                          }
                          active={model._id === deviceInfo?.modelId?._id}
                        >
                          {model.code} ({model.name})
                        </DropdownItem>
                      ))}
                    </DropdownContent>
                  </Dropdown>
                </div>
              </div>

              <div className="form-group">
                <Input
                  type="text"
                  value={deviceInfo?.macAddress}
                  label="MAC"
                  onChange={(value) =>
                    setDeviceInfo((prev) => ({ ...prev, macAddress: value }))
                  }
                />
              </div>
            </div>
          )}

          {/* Logs tab */}
          {activeTab === "logs" && (
            <div className="logs-container">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">{translate("UI.STATUS_HISTORY")}</h6>
                <div className="badge bg-primary-subtle text-primary rounded-pill">
                  {deviceInfo?.logs?.length || 0} {translate("UI.ENTRIES")}
                </div>
              </div>

              {deviceInfo?.logs && deviceInfo.logs.length > 0 ? (
                <div className="status-logs">
                  {deviceInfo.logs
                    .slice()
                    .reverse()
                    .map((log, index) => (
                      <div key={index} className="status-log-item hover-shadow">
                        <div className="status-log-header">
                          <div className="d-flex justify-content-between align-items-center w-100">
                            <Lozenge appearance={getStatusLozenge(log.status)}>
                              {translate(`UI.${log.status.toUpperCase()}`)}
                            </Lozenge>
                            <small className="text-muted">
                              {new Date(log.changedAt).toLocaleString()}
                            </small>
                          </div>
                        </div>
                        {log.notes && (
                          <div className="status-notes mt-2">
                            <small>
                              <FiMessageSquare className="me-1" /> {log.notes}
                            </small>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              ) : (
                <div className="no-logs text-center p-4">
                  <FiInfo
                    className="mb-2"
                    style={{ fontSize: "24px", opacity: 0.5 }}
                  />
                  <p className="mb-0 text-muted">
                    {translate("UI.NO_STATUS_LOGS")}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="form-actions">
            {activeTab === "details" && (
              <Button
                appearance="primary-animated"
                onClick={handleDeviceUpdate}
                loading={loading}
                className="d-flex align-items-center gap-2"
              >
                <FiSave size={16} />
                {translate("UI.SAVE")}
              </Button>
            )}
          </div>
        </div>
      </SidebarContainer>

      {/* Sale Modal */}
      <Modal
        isOpen={showSaleModal}
        onClose={() => setShowSaleModal(false)}
        size="large"
      >
        <ModalHeader>
          <h5 className="mb-0">
            Ürün Stok Aktarımıı ({selectedDevices.length} adet)
          </h5>
        </ModalHeader>
        <ModalBody>
          {/* Sale Information Section */}
          <div className="section-container">
            <div className="section-header">
              <div className="section-icon">
                <FiShoppingCart className="text-primary" />
              </div>
              <div className="section-title">
                <h6 className="mb-1">{translate("UI.SALE_INFORMATION")}</h6>
                <p className="text-muted small mb-0">
                  Stok Aktarımı ve müşteri bilgileri
                </p>
              </div>
            </div>

            <div className="section-content">
              <div className="row g-3">
                <div className="col-md-6">
                  <Label required>Bayi</Label>
                  <div className="form-control-container">
                    <Dropdown
                      trigger={
                        <div className="d-flex align-items-center justify-content-between p-2">
                          <span>
                            {dealers.find(
                              (dealer) => dealer._id === saleForm.dealerId,
                            )?.name || "Bayi Seçin"}
                          </span>
                        </div>
                      }
                    >
                      <DropdownContent>
                        {dealers && dealers.length > 0 ? (
                          dealers.map((dealer) => (
                            <DropdownItem
                              key={dealer._id}
                              onClick={() => handleDealerSelect(dealer._id)}
                              active={dealer._id === saleForm.dealerId}
                            >
                              {dealer.name} ({dealer.code})
                            </DropdownItem>
                          ))
                        ) : (
                          <DropdownItem disabled>Bayi bulunamadı</DropdownItem>
                        )}
                      </DropdownContent>
                    </Dropdown>
                  </div>
                </div>

                <div className="col-md-6">
                  <Label>Firma (Opsiyonel)</Label>
                  <div className="form-control-container">
                    <Dropdown
                      trigger={
                        <div className="d-flex align-items-center justify-content-between p-2">
                          <span>
                            {companies.find(
                              (company) => company._id === saleForm.companyId,
                            )?.name || "Firma Seçin"}
                          </span>
                        </div>
                      }
                    >
                      <DropdownContent>
                        <DropdownItem onClick={() => handleCompanySelect(null)}>
                          <i>Firma Yok</i>
                        </DropdownItem>
                        {companies && companies.length > 0 ? (
                          companies.map((company) => (
                            <DropdownItem
                              key={company._id}
                              onClick={() => handleCompanySelect(company._id)}
                              active={company._id === saleForm.companyId}
                            >
                              {company.name} ({company.code})
                            </DropdownItem>
                          ))
                        ) : (
                          <DropdownItem disabled>Firma bulunamadı</DropdownItem>
                        )}
                      </DropdownContent>
                    </Dropdown>
                  </div>
                </div>

                <div className="col-md-6">
                  <Label>Stok Aktarımı Tarihi</Label>
                  <Input
                    type="date"
                    value={saleForm.saleDate}
                    onChange={handleDateChange}
                    prefix={<FiCalendar className="text-muted" />}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Device Selection Section */}
          <div className="section-container">
            <div className="section-header">
              <div className="section-icon">
                <FiServer className="text-info" />
              </div>
              <div className="section-title">
                <h6 className="mb-1">Seçili Ürünler</h6>
                <p className="text-muted small mb-0">
                  Satılacak ürünler ve fiyat bilgileri
                </p>
              </div>
            </div>

            <div className="section-content">
              <div className="mb-4">
                <Label required>Ürün Başına Fiyat</Label>
                <Input
                  type="number"
                  value={pricePerDevice}
                  onChange={(value) => handlePricePerDeviceChange(value)}
                  prefix={<FiDollarSign className="text-muted" />}
                  placeholder="Ürün başına fiyat"
                />
              </div>

              {selectedDevices.length > 0 && (
                <div className="selected-devices-container">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Label>Seçili Ürünler</Label>
                    <p className="d-flex align-items-center gap-1">
                      <span className="badge bg-primary rounded-pill">
                        {selectedDevices.length}
                      </span>
                      <span>adet</span>
                    </p>
                  </div>

                  <div className="devices-list mb-3">
                    {selectedDevices.map((deviceId) => {
                      const device = devices.find((d) => d._id === deviceId);
                      const deviceModel = deviceModels.find(
                        (model) =>
                          model._id === device?.modelId?._id ||
                          model._id === device?.modelId,
                      );

                      return (
                        <div key={deviceId} className="card mb-2">
                          <div className="card-body p-3">
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <strong>
                                  {device?.hardwareId || "Unknown"}
                                </strong>
                                <div className="text-muted small">
                                  {deviceModel?.code || "Unknown Code"} -{" "}
                                  {deviceModel?.name || "Unknown Model"}
                                </div>
                                <div className="text-muted small">
                                  S/N: {device?.serialNumber || "Unknown"}
                                </div>
                                <div className="small mt-1 badge rounded-pill bg-light text-dark">
                                  {format(pricePerDevice, "currency")}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="total-amount mt-3 p-3 bg-light rounded">
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">Toplam Tutar</h6>
                      <h6 className="mb-0">
                        {format(calculateTotalAmount(), "currency")}
                      </h6>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Payment Information Section */}
          <div className="section-container">
            <div className="section-header">
              <div className="section-icon">
                <FiCreditCard className="text-success" />
              </div>
              <div className="section-title">
                <h6 className="mb-1">Ödeme Bilgileri</h6>
                <p className="text-muted small mb-0">
                  Ödeme yöntemi ve durum bilgileri
                </p>
              </div>
            </div>

            <div className="section-content">
              <div className="row g-3">
                <div className="col-md-4">
                  <Label>Ödeme Yöntemi</Label>
                  <div className="form-control-container">
                    <Dropdown
                      trigger={
                        <div className="d-flex align-items-center justify-content-between p-2">
                          <span>
                            {translate(
                              `UI.${saleForm.paymentMethod.toUpperCase()}`,
                            )}
                          </span>
                        </div>
                      }
                    >
                      <DropdownContent>
                        <DropdownItem
                          onClick={() => handlePaymentMethodChange("cash")}
                          active={saleForm.paymentMethod === "cash"}
                        >
                          {translate("UI.CASH")}
                        </DropdownItem>
                        <DropdownItem
                          onClick={() =>
                            handlePaymentMethodChange("credit_card")
                          }
                          active={saleForm.paymentMethod === "credit_card"}
                        >
                          {translate("UI.CREDIT_CARD")}
                        </DropdownItem>
                        <DropdownItem
                          onClick={() =>
                            handlePaymentMethodChange("bank_transfer")
                          }
                          active={saleForm.paymentMethod === "bank_transfer"}
                        >
                          {translate("UI.BANK_TRANSFER")}
                        </DropdownItem>
                        <DropdownItem
                          onClick={() => handlePaymentMethodChange("check")}
                          active={saleForm.paymentMethod === "check"}
                        >
                          Çek
                        </DropdownItem>
                      </DropdownContent>
                    </Dropdown>
                  </div>
                </div>

                <div className="col-md-4">
                  <Label>Ödeme Durumu</Label>
                  <div className="form-control-container">
                    <Dropdown
                      trigger={
                        <div className="d-flex align-items-center justify-content-between p-2">
                          <Lozenge
                            appearance={getPaymentStatusAppearance(
                              saleForm.paymentStatus,
                            )}
                          >
                            {translate(
                              `UI.${saleForm.paymentStatus.toUpperCase()}`,
                            )}
                          </Lozenge>
                        </div>
                      }
                    >
                      <DropdownContent>
                        <DropdownItem
                          onClick={() => handlePaymentStatusChange("pending")}
                          active={saleForm.paymentStatus === "pending"}
                        >
                          <Lozenge appearance="warning-subtle">
                            {translate("UI.PENDING")}
                          </Lozenge>
                        </DropdownItem>
                        <DropdownItem
                          onClick={() => handlePaymentStatusChange("paid")}
                          active={saleForm.paymentStatus === "paid"}
                        >
                          <Lozenge appearance="success-subtle">
                            {translate("UI.PAID")}
                          </Lozenge>
                        </DropdownItem>
                        <DropdownItem
                          onClick={() => handlePaymentStatusChange("partial")}
                          active={saleForm.paymentStatus === "partial"}
                        >
                          <Lozenge appearance="info-subtle">
                            {translate("UI.PARTIAL")}
                          </Lozenge>
                        </DropdownItem>
                      </DropdownContent>
                    </Dropdown>
                  </div>
                </div>

                <div className="col-md-4">
                  <Label>Stok Aktarımı Durumu</Label>
                  <div className="form-control-container">
                    <Dropdown
                      trigger={
                        <div className="d-flex align-items-center justify-content-between p-2">
                          <Lozenge
                            appearance={getSaleStatusAppearance(
                              saleForm.status,
                            )}
                          >
                            {translate(`UI.${saleForm.status.toUpperCase()}`)}
                          </Lozenge>
                        </div>
                      }
                    >
                      <DropdownContent>
                        <DropdownItem
                          onClick={() => handleSaleStatusChange("pending")}
                          active={saleForm.status === "pending"}
                        >
                          <Lozenge appearance="warning-subtle">
                            {translate("UI.PENDING")}
                          </Lozenge>
                        </DropdownItem>
                        <DropdownItem
                          onClick={() => handleSaleStatusChange("completed")}
                          active={saleForm.status === "completed"}
                        >
                          <Lozenge appearance="success-subtle">
                            {translate("UI.COMPLETED")}
                          </Lozenge>
                        </DropdownItem>
                      </DropdownContent>
                    </Dropdown>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <Label>Notlar</Label>
                <Input
                  as="textarea"
                  rows={3}
                  value={saleForm.notes}
                  onChange={handleNotesChange}
                  placeholder="Stok Aktarımı ile ilgili notlar..."
                />
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button appearance="subtle" onClick={() => setShowSaleModal(false)}>
            İptal
          </Button>
          <Button
            appearance="primary-animated"
            onClick={handleSaleSubmit}
            loading={loading}
            icon={<FiShoppingCart />}
          >
            <FiShoppingCart size={16} className="mr-2" />
            Stok Aktarımını Tamamla
          </Button>
        </ModalFooter>
      </Modal>

      {/* Production Modal */}
      <Modal
        show={showProductionModal}
        onClose={() => setShowProductionModal(false)}
      >
        <ModalHeader>
          <h5 className="mb-0">Yeni Üretim Ekle</h5>
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label required>Model</Label>
            <Select
              value={productionForm.modelId}
              onChange={(e) =>
                setProductionForm((prev) => ({
                  ...prev,
                  modelId: e.target.value,
                }))
              }
            >
              <option value="">Model Seçin</option>
              {models &&
                models.map((model) => (
                  <option key={model._id} value={model._id}>
                    {model.name} ({model.code})
                  </option>
                ))}
            </Select>
          </div>

          <div className="mb-3">
            <Label required>Miktar</Label>
            <Input
              type="number"
              min="1"
              value={productionForm.quantity}
              onChange={(e) =>
                setProductionForm((prev) => ({
                  ...prev,
                  quantity: parseInt(e.target.value) || 1,
                }))
              }
            />
          </div>

          <div className="mb-3">
            <Label>Parti Numarası</Label>
            <Input
              value={productionForm.batchNumber}
              onChange={(e) =>
                setProductionForm((prev) => ({
                  ...prev,
                  batchNumber: e.target.value,
                }))
              }
              placeholder="Örn: BATCH_2024_001"
            />
          </div>

          <div className="mb-3">
            <Label>Notlar</Label>
            <Input
              as="textarea"
              rows={3}
              value={productionForm.notes}
              onChange={(e) =>
                setProductionForm((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              placeholder="Üretim ile ilgili notlar..."
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowProductionModal(false)}
          >
            İptal
          </Button>
          <Button
            color="primary"
            onClick={handleProductionSubmit}
            disabled={!productionForm.modelId || !productionForm.quantity}
          >
            <FiSave size={16} className="me-2" />
            Ekle
          </Button>
        </ModalFooter>
      </Modal>
      {/* Toast Container */}
      <ToastContainer>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            title={toast.message}
            description={toast.description}
            appearance={toast.type}
            onDismissed={() => removeToast(toast.id)}
            onClose={() => removeToast(toast.id)}
            // actions={[
            //   {
            //     content: translate("UI.CLOSE"),
            //     onClick: () => removeToast(toast.id),
            //   },
            // ]}
          />
        ))}
      </ToastContainer>

      <style>
        {`
        .section-container {
          margin-bottom: 1.5rem;
          padding: 1.25rem;
          background: #fff;
          border-radius: 8px;
          border: 1px solid #e9ecef;
        }

        .section-container:last-child {
          margin-bottom: 0;
        }

        .section-header {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          margin-bottom: 1.25rem;
        }

        .section-icon {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--bs-primary-bg-subtle);
          border-radius: 8px;
        }

        .section-icon svg {
          width: 20px;
          height: 20px;
        }

        .section-title h6 {
          font-size: 1rem;
          margin: 0;
          color: var(--bs-gray-900);
        }

        .section-content {
          padding: 0 0.5rem;
        }

        .form-control-container {
          background: #fff;
          border: 1px solid #e2e5e8;
          border-radius: 6px;
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .form-control-container:hover {
          border-color: var(--bs-primary);
        }

        .selected-devices-container {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 1rem;
          margin-top: 1rem;
        }

        .total-amount {
          margin-top: 1rem;
          text-align: right;
          padding: 1rem;
          background: #fff;
          border-radius: 6px;
          border: 1px solid #e9ecef;
        }

        .total-amount h6 {
          margin: 0;
          color: var(--bs-primary);
        }

        .devices-list .card {
          border: 1px solid #e9ecef;
          transition: all 0.2s ease;
        }

        .devices-list .card:hover {
          border-color: var(--bs-primary);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        @media (max-width: 768px) {
          .section-container {
            padding: 1rem;
          }

          .section-header {
            flex-direction: column;
            align-items: center;
            text-align: center;
            gap: 0.5rem;
          }

          .section-icon {
            width: 32px;
            height: 32px;
          }

          .section-icon svg {
            width: 18px;
            height: 18px;
          }
        }

        /* Device Sidebar Styles */
        .device-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          margin-bottom: 1.5rem;
          background: #f8f9fa;
          border-radius: 8px;
        }

        .device-tabs {
          padding: 0 16px;
          margin-bottom: 20px;
        }

        .logs-container {
          padding: 0 16px;
        }

        .custom-tabs {
          display: flex;
          gap: 12px;
          border-bottom: 1px solid #e2e5e8;
          padding-bottom: 0;
          position: relative;
        }

        .tab-button {
          border: none;
          background: transparent;
          padding: 12px 18px;
          color: #6c757d;
          border-radius: 8px 8px 0 0;
          font-weight: 500;
          display: flex;
          align-items: center;
          position: relative;
          transition: all 0.25s ease;
          margin-bottom: -1px;
          cursor: pointer;
          border-bottom: 2px solid transparent;
          box-shadow: none;
        }

        .tab-icon {
          margin-right: 8px;
          font-size: 16px;
        }

        .tab-button:hover {
          color: var(--bs-primary);
          background-color: rgba(12, 132, 121, 0.05);
        }

        .tab-button.active {
          color: var(--bs-primary);
          background-color: #fff;
          border-bottom: 2px solid rgb(156, 165, 154);
          box-shadow: 0 -4px 10px rgba(0, 0, 0, 0.03);
        }

        .tab-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 20px;
          height: 20px;
          padding: 0 6px;
          font-size: 0.75rem;
          font-weight: 600;
          background-color: var(--bs-primary-bg-subtle);
          color: var(--bs-primary);
          border-radius: 10px;
          margin-left: 8px;
        }

        .form-container {
          padding: 0 16px;
        }

        .form-group {
          margin-bottom: 20px;
          position: relative;
        }

        .form-actions {
          margin-top: 24px;
          padding: 16px;
          border-top: 1px solid #e9ecef;
        }

        .form-control {
          display: block;
          width: 100%;
          padding: 0;
          font-size: 0.875rem;
          font-weight: 400;
          line-height: 1.5;
          background-color: #fff;
          background-clip: padding-box;
          border: 1px solid #e2e5e8;
          border-radius: 0.375rem;
          transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
        }

        .form-control:hover {
          border-color: var(--bs-primary);
        }

        .status-logs {
          max-height: 500px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .status-log-item {
          padding: 15px;
          border-radius: 8px;
          background-color: #f8f9fa;
          border-left: 3px solid var(--bs-primary);
          transition: all 0.2s ease;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .status-log-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .status-log-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .status-notes {
          background-color: #fff;
          padding: 10px;
          border-radius: 6px;
          border-left: 3px solid #d1d1d1;
          margin-top: 8px;
        }

        .no-logs {
          background-color: #f8f9fa;
          border-radius: 8px;
          padding: 30px;
          text-align: center;
          color: #6c757d;
          margin-top: 10px;
        }

        /* Empty State Styles */
        .empty-state-container {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 400px;
          padding: 2rem;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-radius: 12px;
          margin: 2rem 0;
          position: relative;
          overflow: hidden;
        }

        .empty-state-container::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(12, 132, 121, 0.05) 0%, transparent 70%);
          animation: float 6s ease-in-out infinite;
        }

        .empty-state-content {
          text-align: center;
          position: relative;
          z-index: 1;
          max-width: 500px;
          animation: fadeInUp 0.6s ease-out;
        }

        .empty-state-icon {
          margin-bottom: 1.5rem;
          position: relative;
        }

        .empty-state-icon svg {
          color: #6c757d;
          opacity: 0.7;
          animation: pulse 2s ease-in-out infinite;
        }

        .empty-state-text {
          margin-bottom: 2rem;
        }

        .empty-state-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: #495057;
          margin-bottom: 0.75rem;
          line-height: 1.3;
        }

        .empty-state-description {
          font-size: 1rem;
          color: #6c757d;
          margin-bottom: 0;
          line-height: 1.5;
        }

        .empty-state-actions {
          display: flex;
          justify-content: center;
          gap: 1rem;
        }

        .empty-state-button {
          padding: 0.75rem 1.5rem;
          font-weight: 500;
          border-radius: 8px;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(12, 132, 121, 0.2);
        }

        .empty-state-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(12, 132, 121, 0.3);
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.7;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.9;
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
          }
        }

        @media (max-width: 768px) {
          .empty-state-container {
            min-height: 300px;
            padding: 1.5rem;
            margin: 1rem 0;
          }

          .empty-state-title {
            font-size: 1.25rem;
          }

          .empty-state-description {
            font-size: 0.9rem;
          }

          .empty-state-button {
            padding: 0.625rem 1.25rem;
            font-size: 0.9rem;
          }
        }
        `}
      </style>
    </AppPage>
  );
};

export default withAuth(ProducedDevices, ["admin", "employee", "user"]);
